import { FC } from 'react';
import { Tag } from '@carbon/react';
import { LaunchLink } from 'components/launch-link';
import type { EvaluationCaseDetail } from './atoms';
import styles from './evaluation-case-domain-chips.module.scss';

interface EvaluationCaseDomainChipsProps {
  detail: EvaluationCaseDetail;
}

/**
 * (13.4) The case's eight domains as chips, in registry order, below the Evaluation Case ComboBox:
 * a case has one scenario per domain. A domain with no scenario, or a retired one, is red. The
 * strip links to the case page.
 *
 * @param {EvaluationCaseDomainChipsProps} props - The fetched case detail.
 * @return {JSX.Element | null} The chips, or null before the detail carries them.
 */
const EvaluationCaseDomainChips: FC<EvaluationCaseDomainChipsProps> = ({
  detail,
}) => {
  if (!detail.scenarioDomains?.length) return null;
  const archived = new Set(detail.archivedDomains ?? []);

  return (
    <div
      className={styles.chips}
      aria-label="Case scenarios"
      data-testid="case-domain-chips"
    >
      {detail.scenarioDomains.map((entry) => {
        const problem = entry.scenarioId === null || archived.has(entry.domain);
        return (
          <Tag
            key={entry.domain}
            size="md"
            type={problem ? 'red' : 'cool-gray'}
            data-testid={`case-domain-chip-${entry.domain}`}
            title={`${entry.label}: ${entry.scenarioName ?? 'no scenario'}`}
          >
            {entry.label}: {entry.scenarioName ?? 'None'}
            {archived.has(entry.domain) ? ' (archived)' : ''}
          </Tag>
        );
      })}
      <LaunchLink
        to={`/evaluation_cases/${detail.id}`}
        label="View Case in a new tab"
        iconSize={12}
      >
        View Case
      </LaunchLink>
    </div>
  );
};

export { EvaluationCaseDomainChips };
